// src/admin/components/JsonViewer.jsx
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Search, ChevronRight, ChevronDown, X } from 'lucide-react';
import { COLORS, FONT_MONO, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

// Light-themed, searchable JSON tree. Replaces the raw <pre> dump (JsonBlock)
// for the superadmin Run Detail intermediate-output sections so they are
// readable and filterable in-place.
//
// Syntax-highlight palette. Token hues come from the Brian UI semantic pairs so
// the tree sits in the same colour family as the rest of the console; the literal
// types keep distinct hues, because telling a string from a number at a glance is
// the whole point of highlighting.
const SYNTAX = {
  key:      COLORS.accent,
  string:   SEMANTIC.verde.fg,
  number:   SEMANTIC.info.fg,
  boolean:  SEMANTIC.errore.fg,
  null:     COLORS.textTertiary,
  index:    COLORS.textSecondary,
  punct:    COLORS.textTertiary,
  muted:    COLORS.textSecondary,
  border:   COLORS.border,
  surface:  COLORS.field,
};
const MONO = FONT_MONO;
const SANS = FONT_SANS;
const INDENT = 16;

function safeStringify(v) {
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

function leafMatches(v, q) {
  if (v === null) return 'null'.includes(q);
  const t = typeof v;
  if (t === 'string' || t === 'number' || t === 'boolean') {
    return String(v).toLowerCase().includes(q);
  }
  return false;
}

// True when this node — or any descendant — matches the (already lowercased) query.
function subtreeMatches(matchKey, value, q) {
  if (matchKey != null && String(matchKey).toLowerCase().includes(q)) return true;
  if (value !== null && typeof value === 'object') {
    if (Array.isArray(value)) return value.some(c => subtreeMatches(null, c, q));
    return Object.entries(value).some(([k, c]) => subtreeMatches(k, c, q));
  }
  return leafMatches(value, q);
}

function countMatches(matchKey, value, q) {
  let n = 0;
  if (matchKey != null && String(matchKey).toLowerCase().includes(q)) n += 1;
  if (value !== null && typeof value === 'object') {
    const entries = Array.isArray(value) ? value.map(c => [null, c]) : Object.entries(value);
    for (const [k, c] of entries) n += countMatches(k, c, q);
  } else if (leafMatches(value, q)) {
    n += 1;
  }
  return n;
}

function Highlight({ text, q }) {
  const s = String(text);
  if (!q) return <>{s}</>;
  const lower = s.toLowerCase();
  const out = [];
  let i = 0;
  while (i < s.length) {
    const idx = lower.indexOf(q, i);
    if (idx === -1) { out.push(s.slice(i)); break; }
    if (idx > i) out.push(s.slice(i, idx));
    out.push(
      <mark key={idx} style={{ background: SEMANTIC.attenzione.bg, color: 'inherit', borderRadius: RADII.controlInner, padding: '0 1px' }}>
        {s.slice(idx, idx + q.length)}
      </mark>
    );
    i = idx + q.length;
  }
  return <>{out}</>;
}

function PrimitiveValue({ value, q }) {
  if (value === null) return <span style={{ color: SYNTAX.null }}>null</span>;
  const t = typeof value;
  if (t === 'boolean') return <span style={{ color: SYNTAX.boolean }}>{String(value)}</span>;
  if (t === 'number')  return <span style={{ color: SYNTAX.number }}>{String(value)}</span>;

  const s = String(value);
  const asBlock = s.length > 80 || s.includes('\n');
  if (asBlock) {
    return (
      <div style={{
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        color: COLORS.ink, background: SYNTAX.surface,
        border: `1px solid ${SYNTAX.border}`, borderRadius: RADII.controlInner,
        padding: '8px 10px', marginTop: '4px',
        fontFamily: SANS, fontSize: '12px', lineHeight: 1.55,
      }}>
        <Highlight text={s} q={q} />
      </div>
    );
  }
  return (
    <span style={{ color: SYNTAX.string }}>
      "<Highlight text={s} q={q} />"
    </span>
  );
}

function Label({ label, kind, q }) {
  if (label == null) return null;
  if (kind === 'index') {
    return <span style={{ color: SYNTAX.index }}>[{label}]</span>;
  }
  return (
    <span style={{ color: SYNTAX.key, fontWeight: 600 }}>
      <Highlight text={label} q={kind === 'key' ? q : ''} />
    </span>
  );
}

function Node({ label, kind, value, depth, query, expandAll }) {
  const { t } = useTranslation('admin');
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const matchKey = kind === 'key' ? label : null;

  const isContainer = value !== null && typeof value === 'object';
  const initialOpen = expandAll === true ? true
    : expandAll === false ? false
    : depth < 2;
  const [open, setOpen] = useState(initialOpen);

  if (searching && !subtreeMatches(matchKey, value, q)) return null;

  const rowStyle = {
    paddingLeft: `${depth * INDENT}px`,
    paddingTop: '1px',
    fontFamily: MONO,
    fontSize: '12px',
    lineHeight: 1.6,
    color: COLORS.ink,
  };

  if (!isContainer) {
    return (
      <div style={rowStyle}>
        <Label label={label} kind={kind} q={searching ? q : ''} />
        {label != null && <span style={{ color: SYNTAX.punct }}>: </span>}
        <PrimitiveValue value={value} q={searching ? q : ''} />
      </div>
    );
  }

  const isArr = Array.isArray(value);
  const entries = isArr
    ? value.map((c, i) => ({ k: i, kind: 'index', v: c }))
    : Object.entries(value).map(([k, v]) => ({ k, kind: 'key', v }));
  const count = entries.length;
  const summary = isArr
    ? t('jsonViewer.items', { count })
    : t('jsonViewer.fields', { count });
  const effectiveOpen = searching ? true : open;

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => { if (!searching) setOpen(o => !o); }}
        onKeyDown={e => { if (!searching && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(o => !o); } }}
        style={{
          ...rowStyle,
          display: 'flex', alignItems: 'center', gap: '3px',
          cursor: searching ? 'default' : 'pointer', userSelect: 'none',
        }}
      >
        {effectiveOpen
          ? <ChevronDown size={13} color={SYNTAX.muted} style={{ flexShrink: 0 }} />
          : <ChevronRight size={13} color={SYNTAX.muted} style={{ flexShrink: 0 }} />}
        <Label label={label} kind={kind} q={searching ? q : ''} />
        {label != null && <span style={{ color: SYNTAX.punct }}>: </span>}
        <span style={{ color: SYNTAX.punct }}>{isArr ? '[' : '{'}</span>
        {!effectiveOpen && (
          <>
            <span style={{ color: SYNTAX.muted, fontStyle: 'italic', fontSize: '11px', margin: '0 4px' }}>{summary}</span>
            <span style={{ color: SYNTAX.punct }}>{isArr ? ']' : '}'}</span>
          </>
        )}
      </div>
      {effectiveOpen && (
        <>
          {entries.map(({ k, kind: ck, v }) => (
            <Node key={k} label={k} kind={ck} value={v} depth={depth + 1} query={query} expandAll={expandAll} />
          ))}
          <div style={{ ...rowStyle, color: SYNTAX.punct }}>{isArr ? ']' : '}'}</div>
        </>
      )}
    </div>
  );
}

const toolBtn = {
  display: 'inline-flex', alignItems: 'center', gap: '4px',
  padding: '4px 8px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.borderStrong}`, background: SYNTAX.surface,
  color: COLORS.ink, fontSize: '11px', cursor: 'pointer',
  fontFamily: SANS,
};

export default function JsonViewer({ value, maxHeight = 420 }) {
  const { t } = useTranslation('admin');
  const [query, setQuery] = useState('');
  const [expandAll, setExpandAll] = useState(null);
  const [treeKey, setTreeKey] = useState(0);
  const [copied, setCopied] = useState(false);

  const q = query.trim().toLowerCase();
  const matchCount = useMemo(() => (q ? countMatches(null, value, q) : 0), [value, q]);

  function setExpansion(v) {
    setExpandAll(v);
    setTreeKey(k => k + 1);
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(safeStringify(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  }

  if (value == null) {
    return (
      <div style={{ padding: '16px', color: SYNTAX.muted, fontStyle: 'italic', fontSize: '13px', fontFamily: SANS }}>
        {t('jsonViewer.notAvailable')}
      </div>
    );
  }

  const isContainer = typeof value === 'object';
  const rootEntries = !isContainer ? null
    : Array.isArray(value)
      ? value.map((c, i) => ({ k: i, kind: 'index', v: c }))
      : Object.entries(value).map(([k, v]) => ({ k, kind: 'key', v }));

  return (
    <div style={{ border: `1px solid ${SYNTAX.border}`, borderRadius: RADII.controlInner, overflow: 'hidden', background: SYNTAX.surface }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
        padding: '8px 10px', borderBottom: `1px solid ${SYNTAX.border}`,
        background: SYNTAX.surface,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px', flex: '1 1 200px',
          background: SYNTAX.surface, border: `1px solid ${SYNTAX.border}`,
          borderRadius: RADII.controlInner, padding: '5px 8px',
        }}>
          <Search size={13} color={SYNTAX.muted} style={{ flexShrink: 0 }} />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('jsonViewer.searchPlaceholder')}
            style={{
              border: 'none', outline: 'none', background: 'transparent',
              fontSize: '12px', fontFamily: SANS, color: COLORS.ink,
              flex: 1, minWidth: 0,
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              title={t('jsonViewer.clearSearch')}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex', color: SYNTAX.muted }}
            >
              <X size={13} />
            </button>
          )}
        </div>
        {q && (
          <span style={{ fontSize: '11px', color: SYNTAX.muted, fontFamily: SANS, whiteSpace: 'nowrap' }}>
            {t('jsonViewer.matches', { count: matchCount })}
          </span>
        )}
        <button type="button" onClick={() => setExpansion(true)}  style={toolBtn}>{t('jsonViewer.expandAll')}</button>
        <button type="button" onClick={() => setExpansion(false)} style={toolBtn}>{t('jsonViewer.collapseAll')}</button>
        <button
          type="button"
          onClick={handleCopy}
          style={{ ...toolBtn, color: copied ? SEMANTIC.verde.fg : COLORS.ink }}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? t('shared.copied') : t('shared.copy')}
        </button>
      </div>

      {/* Tree */}
      <div style={{ maxHeight, overflow: 'auto', padding: '10px 12px' }}>
        {q && matchCount === 0 ? (
          <div style={{ color: SYNTAX.muted, fontStyle: 'italic', fontSize: '12px', fontFamily: SANS, padding: '4px 0' }}>
            {t('jsonViewer.noMatch', { query: query.trim() })}
          </div>
        ) : isContainer ? (
          <div key={treeKey}>
            {rootEntries.map(({ k, kind, v }) => (
              <Node key={k} label={k} kind={kind} value={v} depth={0} query={query} expandAll={expandAll} />
            ))}
          </div>
        ) : (
          <Node key={treeKey} label={null} kind={null} value={value} depth={0} query={query} expandAll={expandAll} />
        )}
      </div>
    </div>
  );
}
